import React, { Component } from 'react'
import {observer} from 'mobx-react'
import { t } from 'ttag'
import _debug from 'debug'

import { displayError } from '../utils/Errors'
import { PassageVideo, Passage, IDrawablePassageGloss, Project } from '../../models3/ProjectModels'
import { makeObservable } from 'mobx';
import { DeleteButton, PlayButton, GlossPositionAdjustTimeButtons, PreviousSegmentButton, NextSegmentButton } from '../utils/Buttons'
import { SearchBox } from '../utils/SearchBox'
import { RefRange } from '../../scrRefs/RefRange'
import { Checkbox } from '../utils/Checkbox'
import { FontSizeSelectorButton } from '../utils/FontSizeSelector'
import { GlossInfo } from '../../models3/GlossInfo'

// Edit name (rename) existing Gloss
// It must be a valid Gloss name and not create a duplicate Gloss name
const log = _debug('sltt:GlossEditor')

const _fontSizeToTextZoom = [
    { fontSize: 12, textZoom: 1.0 },
    { fontSize: 14, textZoom: 1.3 },
    { fontSize: 16, textZoom: 1.6 },
]

function fontSizeToTextZoom(fontSize: number) {
    const found = _fontSizeToTextZoom.find(f => f.fontSize === fontSize) ?? _fontSizeToTextZoom[0]
    return found.textZoom
}

function textZoomToFontSize(textZoom: number) {
    const found = _fontSizeToTextZoom.find(f => f.textZoom === textZoom) ?? _fontSizeToTextZoom[0]
    return found.fontSize
}

interface IGlossEditor {
    passage: Passage | null,
    passageVideo: PassageVideo | null,
    project: Project,
    resetCurrentTime: (newTime: number) => void,
    stop: () => void,
    play: (startTime?: number, endPosition?: number, resetTime?: number) => void,
    drawables: IDrawablePassageGloss[],
    onDelete: () => Promise<void>,
    onDone: () => void,
    glossInfo: GlossInfo,
}

class GlossEditor extends Component<IGlossEditor> {
    glossEditorRef = React.createRef<HTMLDivElement>()

    constructor(props: IGlossEditor) {
        super(props)
        makeObservable(this);

        this.onPlay = this.onPlay.bind(this)
        this.saveChanges = this.saveChanges.bind(this)
        this.clickOutside = this.clickOutside.bind(this)
    }

    componentDidMount() {
        document.addEventListener('mousedown', this.clickOutside)
        document.addEventListener('touchstart', this.clickOutside)
    }

    componentWillUnmount() {
        document.removeEventListener('mousedown', this.clickOutside)
        document.removeEventListener('touchstart', this.clickOutside)
    }

    clickOutside(event: any) {
        log('clickOutside', event.target)
        const { onDone, stop, glossInfo } = this.props
        const { glossEditorRef } = this
        const { glossLabelRef } = glossInfo
        
        // Ignore clicks inside the gloss editor or gloss label
        if (glossEditorRef.current?.contains(event.target) || 
                glossLabelRef.current?.contains(event.target)) { 
            return 
        }
        
        log('clickOutside 2')
        this.saveChanges().catch(displayError)
        
    }

    saveChanges = async () => {
        const { glossInfo } = this.props
        await glossInfo.save()
        glossInfo.setGlossIndex(-1) // turn off editing
        stop()
        this.props.onDone()
    }

    cancelChanges = () => {
        const { glossInfo, stop, onDone } = this.props
        glossInfo.cancel()
        stop()
        onDone()
    }

    render() {
        let { props, glossEditorRef } = this
        let { onDelete, passage, passageVideo, stop, onDone, project, glossInfo } = props
        const { textZoom, hasLabel, position, minPosition, maxPosition, text } = glossInfo
        
        if (!passage || !passageVideo) {
            return null
        }

        let delta = 0.05

        //{ t`Type gloss. <Enter> = save.` } <br />
        //{ t`<Esc> = cancel. <Tab> = next sign.` }

        let refRanges: RefRange[] = []
        if (passage) {
            refRanges = passage.references
        }

        let glosses = project.getGlossesFromVerses(refRanges)

        return (
            <div className="gloss-editor" ref={glossEditorRef}>
                <div className="gloss-pre-commands">
                    <PlayButton
                        tooltip={/* translator: important */ t`Play sign ( ] ), play longer ( } ).`}
                        className="gloss-play-button"
                        enabled={true}
                        selectionPresent={false}
                        onClick={() => this.onPlay(.25)} />
                    <GlossPositionAdjustTimeButtons
                        previousFrameEnabled={position - delta >= minPosition}
                        nextFrameEnabled={position + delta <= maxPosition}
                        adjustCurrentTime={(delta: number) => (
                            this.adjustPosition.bind(this)(delta)
                        )} />
                    <DeleteButton
                        className="gloss-delete"
                        tooltip={/* translator: important */ t`Delete gloss`}
                        enabled={true}
                        onClick={onDelete} />
                </div>
                <div className="gloss-editor-input">
                    <PreviousSegmentButton
                        enabled={this.gotoPreviousGlossEnabled()}
                        onClick={() => this.gotoPreviousGloss()}
                        tooltip={/* translator: important */ t`Go to previous gloss (Shift+Tab).`} />
                    <SearchBox
                        searchParameter={text}
                        options={glosses}
                        onEnter={() => { this.saveChanges().catch(displayError) } }
                        onEscape={() => { this.cancelChanges() }}
                        searchParameterChanged={(newReferences: string) => {
                            glossInfo.setText(newReferences)
                        }}
                        tooltip=''
                        keyboardShortcuts={[
                            {
                                key: ']',
                                metaKey: false,
                                shiftKey: false,
                                handler: (e) => {
                                    e.preventDefault()
                                    this.onPlay(.25)
                                }
                            },
                            {
                                key: '}',
                                metaKey: false,
                                shiftKey: true,
                                handler: (e) => {
                                    e.preventDefault()
                                    this.onPlay(1.5)
                                }
                            },
                            {
                                key: 'ArrowLeft',
                                metaKey: false,
                                shiftKey: false,
                                handler: (e) => {
                                    if (e.metaKey || e.ctrlKey) {
                                        e.preventDefault()
                                        this.adjustPosition(-2.0 / 30.0)
                                    }
                                }
                            },
                            {
                                key: 'ArrowRight',
                                metaKey: false,
                                shiftKey: false,
                                handler: (e) => {
                                    if (e.metaKey || e.ctrlKey) {
                                        e.preventDefault()
                                        this.adjustPosition(2.0 / 30.0)
                                    }
                            }
                            },
                            {
                                key: 'Tab',
                                metaKey: false,
                                shiftKey: false,
                                handler: (e) => {
                                    e.preventDefault()
                                    this.gotoNextGloss()
                                }
                            },
                            {
                                key: 'Tab',
                                metaKey: false,
                                shiftKey: true,
                                handler: (e) => {
                                    e.preventDefault()
                                    this.gotoPreviousGloss()
                                }
                            },
                        ]}
                        disableTab
                        autoFocus
                    />
                    <NextSegmentButton
                        enabled={this.gotoNextGlossEnabled()}
                        onClick={() => this.gotoNextGloss()}
                        tooltip={t`Go to next gloss (Tab).`} />
                </div>
                <div className="gloss-post-commands">
                    <Checkbox className="gloss-label-checkbox" 
                        setChecked={_checked => glossInfo.setHasLabel(_checked)}
                        onEnter={() => this.saveChanges().catch(displayError)}
                        onEscape={() => this.cancelChanges()}
                        checked={hasLabel} 
                        tooltip={t`Show gloss as label`}/>
                    <FontSizeSelectorButton
                        hasLabel={hasLabel}
                        fontSize={textZoomToFontSize(textZoom)}
                        setFontSize={(_fontSize: number) => {
                            const _textZoom = fontSizeToTextZoom(_fontSize)
                            glossInfo.setTextZoom(_textZoom)
                        }}
                        allowedFontSizes={_fontSizeToTextZoom.map(f => f.fontSize)}
                        sampleText={text}
                        onClose={() => {}} 
                        tooltip={t`Select label font size.`} />
                </div>
            </div>
        )
    }

    adjustPosition(adjustment: number) {
        log(`adjustPosition ${adjustment}`)
        const { glossInfo, resetCurrentTime } = this.props
        const { position } = glossInfo
        glossInfo.setPosition(position + adjustment)
    
        // Make video show correct frame for start of gloss
        resetCurrentTime(glossInfo.time())
    }

    gotoPreviousGlossEnabled = () => {
        let { glossIndex } = this.props.glossInfo
        return glossIndex > 0
    }

    gotoPreviousGloss = () => {
        if (!this.gotoPreviousGlossEnabled()) return
        const { glossIndex, setGlossIndex, save } = this.props.glossInfo
        save().catch(displayError) // nop, if editing not in progress
        setGlossIndex(glossIndex - 1)
        this.onPlay(0)
    }

    gotoNextGlossEnabled = () => {
        const { glossInfo } = this.props
        const { glossIndex, visibleGlosses } = glossInfo
        return glossIndex < visibleGlosses.length - 1
    }

    gotoNextGloss = () => {
        if (!this.gotoNextGlossEnabled()) return
        const { glossInfo } = this.props
        const { glossIndex } = glossInfo
        glossInfo.save().catch(displayError) // nop, if editing not in progress
        glossInfo.setGlossIndex(glossIndex + 1)
        this.onPlay(0)
    }

    onPlay(margin: number) {
        const { play, glossInfo } = this.props
        const time = glossInfo.time()
        const duration = glossInfo.duration()
        log(`onPlay time=${time.toFixed(1)}, duration=${duration.toFixed(1)}`)

        const start = Math.max(0, time-margin)
        play(start, time + duration + margin, time)
    }
}

export default observer(GlossEditor)